<template>
    <div class="grow" :class="{ 'legacy-calendar': !tonalThemeEnabled }">
        <div class="flex flex-col md:flex-row items-stretch gap-4">
            <HomepageCalendarInsight v-if="calendarInsightsEnabled" class="order-2 md:order-1 md:min-h-full w-full md:w-1/3" />
            <component
                :is="calendarContainer"
                v-bind="calendarContainerProps"
                class="order-1 md:order-2 flex-1 min-w-0 pt-6 px-6 pb-11 relative flex flex-col"
                :class="{ 'bg-white rounded-lg border border-valence-grey-300': !tonalThemeEnabled }">
                <div v-if="showTitle" class="mb-2 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <div class="flex flex-row gap-1 items-start md:grow">
                        <i class="bi bi-calendar text-lg/none mr-1 mt-0.5" aria-hidden="true"></i>
                        <h3 class="scaffold-body grow" :class="tonalThemeEnabled ? 'text-ink' : 'text-valence-grey-850'">Prepare and debrief meetings</h3>
                    </div>
                    <Transition name="fade">
                        <div v-if="isFetching && hasInitialEvents" class="absolute top-2 right-2 z-10" title="Refreshing calendar data">
                            <LoadingSpinner class="h-4 w-4" />
                        </div>
                    </Transition>
                    <div class="flex justify-end items-center gap-2 md:gap-4 shrink-0">
                        <Transition name="fade">
                            <BaseButton
                                v-if="calendarCoachUseCase && hasEvents"
                                :theme="tonalThemeEnabled ? 'primary' : 'neutral'"
                                :size="tonalThemeEnabled ? 'sm' : undefined"
                                class="animate-shadow-pulse"
                                :class="{
                                    'whitespace-nowrap px-6 py-1 rounded-3xl body-small font-semibold bg-white border border-[#DFDFDF] text-valence-grey-850 hover:bg-[#FAFAFA]':
                                        !tonalThemeEnabled,
                                }"
                                @click="() => handleCalendarCoachClick(calendarCoachUseCase!)"
                                @mouseenter="handleCoachHover"
                                @mouseleave="handleCoachHoverEnd"
                                @focus="handleCoachHover"
                                @blur="handleCoachHoverEnd">
                                {{ calendarCoachUseCase?.label }}
                            </BaseButton>
                            <BaseButton v-else-if="hasEvents && tonalThemeEnabled" theme="neutral" size="sm" @click="scrollToActiveEvent('smooth')">
                                {{ isActiveEventLive ? "Current" : "Next" }} Meeting
                            </BaseButton>
                            <button
                                v-else-if="hasEvents"
                                type="button"
                                class="border rounded-full px-3.5 body-small text-[#595959] border-valence-grey-300 hover:border-[#EFEFEF] hover:bg-[#EFEFEF] transition-colors whitespace-nowrap"
                                @click="scrollToActiveEvent('smooth')">
                                {{ isActiveEventLive ? "Current" : "Next" }} Meeting
                            </button>
                        </Transition>
                        <BaseButton
                            v-if="showSettings && tonalThemeEnabled"
                            theme="neutral"
                            size="sm"
                            icon-only
                            title="Calendar Settings"
                            aria-label="Calendar Settings"
                            @click="isSettingsOpen = true">
                            <i class="bi bi-gear text-lg/none" aria-hidden="true"></i>
                        </BaseButton>
                        <button
                            v-else-if="showSettings"
                            type="button"
                            class="bi bi-gear text-lg/none"
                            title="Calendar Settings"
                            aria-label="Calendar Settings"
                            @click="isSettingsOpen = true"></button>
                        <CalendarIntegrationSettings v-if="showSettings" v-model:open="isSettingsOpen" @close="isSettingsOpen = false" @timezone-updated="onTimezoneUpdated" />
                    </div>
                </div>
                <hr v-if="showTitle" class="mt-2 mb-3" :class="tonalThemeEnabled ? 'border-border' : 'border-valence-grey-250'" />
                <div ref="scrollContainer" class="h-87.5 overflow-y-auto overflow-x-hidden scroll-smooth relative" :class="{ 'scrollbar-hide': hideScrollbars }">
                    <UserCalendarSkeleton v-if="isFetching && !hasInitialEvents" />
                    <CalendarEvents v-else ref="calendarEvents" :calendar-events="previewCalendarEvents" :show-header-background="showHeaderBackground" />
                </div>
            </component>
        </div>
    </div>
    <AttendeePeopleDialog v-model:open="useCalendarStore.showAttendeesDialog" :attendees="useCalendarStore.dialogAttendees" />
</template>

<script setup lang="ts">
import { isBeforeNow, isBetween } from "/js/dateUtils";
import { useIntervalFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import HomepageCalendarInsight from "~vue/CalendarInsights/HomepageCalendarInsight.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import AttendeePeopleDialog from "~vue/components/CalendarIntegration/AttendeePeopleDialog.vue";
import CalendarEvents from "~vue/components/CalendarIntegration/CalendarEvents.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import UserCalendarSkeleton from "~vue/components/CalendarIntegration/UserCalendarSkeleton.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { useCalendarStore } from "~vue/stores/calendarStore";
import type { CalendarIntegrationViewEvent, ProcessedCalendarEvent } from "~vue/types/calendar_integration";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { DateTime } from "luxon";
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

interface Props {
    initialCalendarEvents: CalendarIntegrationViewEvent[] | null;
    calendarCoachUseCase: ChatEntranceUseCaseData | null;
    calendarInsightsEnabled: boolean;
    showSettings?: boolean;
    showTitle?: boolean;
    showHeaderBackground?: boolean;
    hideScrollbars?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    initialCalendarEvents: null,
    calendarCoachUseCase: null,
    showSettings: true,
    showTitle: true,
    showHeaderBackground: true,
    hideScrollbars: false,
});

// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.
const calendarContainer = computed(() => (tonalThemeEnabled.value ? BaseCard : "div"));
const calendarContainerProps = computed(() => (tonalThemeEnabled.value ? { padding: "none" as const } : {}));

const emit = defineEmits(["coach-hover", "coach-hover-end"]);

onMounted(() => logUserInteraction(AmplitudeEventNames.CALENDAR_SECTION_SHOWN));

const isSettingsOpen = ref(false);
const showSettings = computed(() => props.showSettings);
const showTitle = computed(() => props.showTitle);
const showHeaderBackground = computed(() => props.showHeaderBackground);
const hideScrollbars = computed(() => props.hideScrollbars);
const hasInitialEvents = computed(() => props.initialCalendarEvents && props.initialCalendarEvents.length > 0);
const hasEvents = computed(() => !isFetching.value && Object.keys(previewCalendarEvents.value).length > 0);
const formatToStr = (date: DateTime) => date.toFormat("yyyy-MM-dd");

// Initialize with cached events if available
const usersCalendarEvents = ref(props.initialCalendarEvents || []);

const setActiveEvent = () => {
    const activeEvent = usersCalendarEvents.value?.find((event) => event.end_time && !isBeforeNow(DateTime.fromISO(event.end_time)) && !event.all_day);
    if (activeEvent) {
        useCalendarStore.setActiveEvent(activeEvent);
    }
};

// Necessary to call this here so that by the time we get to calling .setActiveElement() in `CalEventCollapsible.vue`,
// we have an active event set.
setActiveEvent();

const previewCalendarEvents = computed(() => {
    if (!usersCalendarEvents.value || !Array.isArray(usersCalendarEvents.value)) {
        return {};
    }

    const eventsByDate: Record<string, ProcessedCalendarEvent[]> = {};

    usersCalendarEvents.value.forEach((event) => {
        try {
            if (!event.start_time || !event.end_time) {
                logError("Calendar event missing start or end time", { context: { event } });
                return;
            }
            const startTime = DateTime.fromISO(event.start_time, { setZone: true });
            const endTime = DateTime.fromISO(event.end_time, { setZone: true });
            const dateStr = formatToStr(startTime);

            if (!eventsByDate[dateStr]) {
                eventsByDate[dateStr] = [];
            }

            eventsByDate[dateStr].push({
                provider: event.provider,
                event_id: event.event_id,
                title: event.subject || "(no title)",
                description: event.description,
                attendees: event.attendees,
                startTime,
                endTime,
                meeting_tip: event.meeting_tip,
                has_coaching_session: event.has_coaching_session,
            });
        } catch (error) {
            logError(error);
        }
    });

    return eventsByDate;
});

const scrollContainer = useTemplateRef("scrollContainer");
const { data: fetchedCalendarEvents, isFetching, onFetchResponse, execute: fetchUsersCalendarEvents } = useFetch("/api/calendar_integration/events/").json();

const onTimezoneUpdated = () => {
    fetchUsersCalendarEvents();
};

// Handle successful fetch - update events
onFetchResponse(() => {
    if (fetchedCalendarEvents.value) {
        usersCalendarEvents.value = fetchedCalendarEvents.value;
        setActiveEvent();
    }
});

const { pause } = useIntervalFn(() => {
    // polls active events every minute
    setActiveEvent();
}, 60000);

watch(
    () => useCalendarStore.activeElement,
    () => {
        scrollToActiveEvent();
    },
);

const scrollToActiveEvent = (behavior: ScrollBehavior = "instant") => {
    const activeElement = useCalendarStore.activeElement;
    const container = scrollContainer.value;
    if (activeElement && container) {
        const scrollMargin = Number.parseFloat(getComputedStyle(activeElement as unknown as Element).scrollMarginTop) || 0;
        container.scrollTo({
            top: activeElement.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - scrollMargin,
            behavior,
        });
    }
};

const isActiveEventLive = computed(() => {
    if (!useCalendarStore.activeEvent) {
        return false;
    }
    const { start_time, end_time } = useCalendarStore.activeEvent;
    if (!start_time || !end_time) {
        return false;
    }
    return isBetween(DateTime.fromISO(start_time), DateTime.fromISO(end_time));
});

const handleCalendarCoachClick = (useCase: ChatEntranceUseCaseData) => {
    logUserInteraction(AmplitudeEventNames.CALENDAR_COACH_CTA_CLICK, { call_code: useCase.call_code });
    openUrl("/coach/start/", {
        queryParams: {
            chat_source: useCase.chat_source,
            script: useCase.call_code,
        },
    });
};

const handleCoachHover = () => {
    emit("coach-hover", props.calendarCoachUseCase);
};

const handleCoachHoverEnd = () => {
    emit("coach-hover-end");
};

onUnmounted(() => {
    pause();
});
</script>

<style scoped>
.legacy-calendar .fade-enter-active,
.legacy-calendar .fade-leave-active {
    transition: opacity 200ms ease-in-out;
}
</style>
